import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { identifyUser, resetAnalytics } from '../lib/analytics';

interface User {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  googleSignIn: (credential: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.me()
      .then((res) => {
        setUser(res.user);
        identifyUser(res.user.id, { email: res.user.email, name: res.user.name });
      })
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login({ email, password });
    localStorage.setItem('token', res.token);
    setUser(res.user);
    identifyUser(res.user.id, { email: res.user.email, name: res.user.name });
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    const res = await api.register({ email, password, name });
    localStorage.setItem('token', res.token);
    setUser(res.user);
    identifyUser(res.user.id, { email: res.user.email, name: res.user.name });
  }, []);

  const googleSignIn = useCallback(async (credential: string) => {
    const res = await api.googleSignIn(credential);
    localStorage.setItem('token', res.token);
    setUser(res.user);
    identifyUser(res.user.id, { email: res.user.email, name: res.user.name });
  }, []);

  const logout = useCallback(() => {
    // Best-effort server-side session revocation; don't block on failure.
    api.logout().catch(() => {
      // Already signed out or network unreachable — local sign-out is authoritative for UX.
    });
    localStorage.removeItem('token');
    setUser(null);
    resetAnalytics();
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.me();
    setUser(res.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleSignIn, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
